import React, { useState, useRef } from "react";
import { Search, Loader, X, MapPin, Phone, Mail, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import "@/components/consulta-cnpj.css";

/**
 * Consulta pública de CNPJ — amostra de credibilidade na ComprarLeadsPage.
 *
 * Fluxo (padrão CNPJ Biz): campo compacto no header → digita CNPJ → o card
 * vira a ficha da empresa → botão "pesquisar outro CNPJ" reseta.
 *
 * Só mostra campos que EXISTEM na base (DuckDB). Não inventa Inscrição
 * Estadual / Simples / situação cadastral, que a base não tem.
 *
 * Backend: POST /api/cnpj/consulta (público, 15/IP/dia).
 */

function mascaraCnpj(v) {
  const d = v.replace(/\D/g, "").slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}

export default function ConsultaCnpjBox() {
  const [cnpj, setCnpj] = useState("");
  const [loading, setLoading] = useState(false);
  const [ficha, setFicha] = useState(null);
  const [erro, setErro] = useState("");
  const inputRef = useRef(null);

  const consultar = async () => {
    const digitos = cnpj.replace(/\D/g, "");
    if (digitos.length !== 14) {
      setErro("Digite os 14 dígitos do CNPJ.");
      return;
    }
    setLoading(true);
    setErro("");
    try {
      const { data } = await api.post("/cnpj/consulta", { cnpj: digitos });
      setFicha(data.resultado);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setErro(
        status === 404
          ? detail || "CNPJ não localizado na base de empresas ativas."
          : status === 429
          ? detail || "Você atingiu o limite de consultas de hoje."
          : status === 400
          ? "CNPJ inválido — confira os números."
          : "Não consegui consultar agora. Tente de novo em instantes."
      );
    } finally {
      setLoading(false);
    }
  };

  const novaConsulta = () => {
    setFicha(null);
    setErro("");
    setCnpj("");
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // ── Ficha da empresa (após consultar) ──
  if (ficha) {
    return (
      <div className="ccnpj-card ccnpj-ficha">
        <div className="ccnpj-ficha-head">
          <div className="ccnpj-ficha-titulo">
            <strong>{ficha.razao_social || "—"}</strong>
            {ficha.nome_fantasia && ficha.nome_fantasia !== ficha.razao_social && (
              <span className="ccnpj-fantasia">{ficha.nome_fantasia}</span>
            )}
          </div>
        </div>

        <div className="ccnpj-badges-topo">
          <span className="ccnpj-cnpj-badge">{ficha.cnpj}</span>
          {ficha.situacao && (
            <span className={`ccnpj-situacao ${ficha.situacao.toLowerCase() === "ativa" ? "ccnpj-situacao-ativa" : "ccnpj-situacao-outra"}`}>
              {ficha.situacao}
            </span>
          )}
        </div>

        <div className="ccnpj-linhas">
          {ficha.cnae?.descricao && (
            <div className="ccnpj-linha">
              <span className="ccnpj-k">Atividade</span>
              <span className="ccnpj-v">
                {ficha.cnae.descricao}
                {ficha.cnae.codigo && <em className="ccnpj-cnae-cod"> ({ficha.cnae.codigo})</em>}
              </span>
            </div>
          )}
          {ficha.endereco && (
            <div className="ccnpj-linha">
              <MapPin size={13} className="ccnpj-mini" />
              <span className="ccnpj-v">{ficha.endereco}</span>
            </div>
          )}
          {ficha.telefone && (
            <div className="ccnpj-linha">
              <Phone size={13} className="ccnpj-mini" />
              <span className="ccnpj-v">{ficha.telefone}</span>
            </div>
          )}
          {ficha.email && (
            <div className="ccnpj-linha">
              <Mail size={13} className="ccnpj-mini" />
              <span className="ccnpj-v">{ficha.email}</span>
            </div>
          )}
        </div>

        <div className="ccnpj-tags">
          {ficha.porte && <span className="ccnpj-tag">{ficha.porte}</span>}
          {ficha.natureza_juridica && <span className="ccnpj-tag">{ficha.natureza_juridica}</span>}
          {ficha.opcao_mei === true && <span className="ccnpj-tag ccnpj-tag-mei">MEI</span>}
          {ficha.opcao_simples === true && <span className="ccnpj-tag ccnpj-tag-mei">Simples</span>}
          {ficha.data_abertura && <span className="ccnpj-tag">Desde {ficha.data_abertura}</span>}
        </div>

        <button className="ccnpj-nova" onClick={novaConsulta}>
          <RotateCcw size={13} /> Pesquisar outro CNPJ
        </button>
      </div>
    );
  }

  // ── Campo de busca (estado inicial) ──
  return (
    <div className="ccnpj-card">
      <div className="ccnpj-busca">
        <button className="ccnpj-search-btn" onClick={consultar} disabled={loading} aria-label="Consultar CNPJ">
          <Search size={15} className="ccnpj-search-icon" strokeWidth={2} />
        </button>
        <input
          ref={inputRef}
          className="ccnpj-input"
          value={cnpj}
          onChange={(e) => { setCnpj(mascaraCnpj(e.target.value)); setErro(""); }}
          onKeyDown={(e) => e.key === "Enter" && consultar()}
          placeholder="Consulte um CNPJ"
          inputMode="numeric"
          disabled={loading}
        />
        {loading ? (
          <Loader size={15} className="ccnpj-spin ccnpj-trailing" strokeWidth={2} />
        ) : cnpj ? (
          <button className="ccnpj-clear" onClick={() => { setCnpj(""); setErro(""); }} aria-label="Limpar">
            <X size={14} strokeWidth={2} />
          </button>
        ) : null}
      </div>
      {erro && <div className="ccnpj-erro">{erro}</div>}
    </div>
  );
}
